import React, { useState } from "react";
import { AlertProvider, Button, ErrorScope, Modal, Spinner } from "acma-spa";
import { ClientApiProps } from "../common";
import { useNavigate } from "react-router-dom";
import { getApiClient } from "../api";
export interface ConsentProps extends ClientApiProps {
  onChange: () => any;
  readonly: boolean;
}

export const Consent: React.FC<ConsentProps> = (props) => {
  const [error, setError] = useState<string | undefined>(undefined);
  const [spinner, setSpinner] = useState<string>("");

  const navigate = useNavigate();

  const onConfirm = async () => {
    try {
      setSpinner("Updating...");

      const [client, abort] = getApiClient(
        props.baseUrl,
        props.b2cDomainName,
        props.msalClient
      );
      await client.consent();

      props.onChange();
    } catch (e: any) {
      setError(e.message ? e.message : "Unexpected error");
    } finally {
      window.scroll(0, 0);
      setSpinner("");
    }
  };

  const onBack = async () => {
    navigate(-1);
  };

  const onOk = async () => {
    props.onChange();
    window.scroll(0, 0);
    setSpinner("");
  }

  return (
    <>
      {error ? (
        <AlertProvider
          messages={[error]}
          noHeading={true}
          color="danger"
          dismiss={true}
        />
      ) : (
        <></>
      )}

      <h1>Sender ID registry</h1>
      <Spinner isLoading={spinner ? true : false} loadingText={spinner}>
        <h2>Terms of Use</h2>

        <h3>About the ACMA SMS Sender ID Registry</h3>
        <p>
          The objective of the SMS Sender ID Registry (registry) is to protect
          legitimate alphanumeric message headers (alpha tags) of brands and
          agencies from impersonation by scammers and to prevent consumers from
          receiving such scam messages.
        </p>
        <p>
          The Australian Communications and Media Authority (ACMA) is
          implementing the registry in phases, so that some protections are in
          place as quickly as possible to disrupt scam activity. Phase 1 of the
          registry involves the consolidation, centralisation and expansion of
          existing arrangements that some mobile network operators already have
          in place with brands and agencies to protect alpha tags from
          impersonation by scammers. Participation in the first phase is
          voluntary for telcos and brands and relies on mobile network operators
          matching an alpha tag with a single source of origin.
        </p>
        <p>
          Implementation of Phase 2 of the registry is subject to the impact and
          success of phase 1 and the introduction of legislation that may
          require compliance with an Australia - wide registry.
        </p>

        <h3>Disclaimer</h3>
        <p>
          The ACMA has exercised reasonable care in preparing and publishing the
          registry. However, given the nature of the information it contains and
          the variety of sources from which that information is drawn, the ACMA
          does not warrant that the information available in the registry is
          accurate, current or complete.
        </p>
        <p>
          No liability is or will be accepted by the ACMA or the Commonwealth of
          Australia, or its officers, employees or agents for any loss suffered,
          whether arising directly or indirectly, due to reliance on information
          in the registry. This also includes but is not limited to any loss or
          damage arising in reliance on incomplete or inaccurate information
          that may be contained in the registry.
        </p>

        <h3>General Terms and Conditions for using the registry</h3>
        <p>You agree that you will not knowingly:</p>
        <ul>
          <li>breach these Terms of Use;</li>
          <li>
            obtain or attempt to obtain, unauthorised access to any part of this
            registry;
          </li>
          <li>
            attempt to deface, hack or otherwise interfere with the registry or
            any material, information or content contained in or displayed on
            the registry;
          </li>
          <li>use or attempt to use the registry for any unlawful purpose;</li>
          <li>
            use or attempt to use the registry for any purpose for which it was
            not intended;
          </li>
          <li>
            attempt to pass yourself or another user off as another person or
            entity when using the registry;
          </li>
          <li>
            intentionally or negligently input inaccurate data to the registry;
          </li>
          <li>
            transfer or attempt to transfer any material of any kind to the
            registry website which contains a virus, malicious computer code or
            any harmful component.
          </li>
        </ul>
        <p>
          These terms and conditions may change from time to time. While the
          ACMA will endeavour to ensure that changes to the terms and conditions
          are notified in the registry portal, by continuing to access the
          registry, you agree to be bound by the terms and conditions as they
          exist at the time of each access.
        </p>

        <h3>Using the registry</h3>
        <p>
          By accessing and using the registry you acknowledge and agree to these
          Terms of Use. By continuing to access or use the registry you are
          confirming that:
        </p>
        <ul>
          <li>
            You are authorised to use or access the registry and, if you are
            using or accessing the registry on behalf of another person or
            entity, you are authorised to do so,
          </li>
          <li>
            You have read, considered and understood these Terms of Use and
            acknowledge and agree to them,
          </li>
          <li>
            You will exercise your own independent skill or judgement, or seek
            professional advice, before inputting data on the registry or using
            any of the information available in the registry.
          </li>
          <li>
            You accept all liability for any loss, damage, cost or expense that
            you may incur, whether arising directly or indirectly, due to
            reliance upon the information available in the registry and will
            make no claim against the ACMA.
          </li>
        </ul>

        {props.readonly && (
          <>
            <Button
              id="ok"
              label="OK"
              onClick={() => onOk()}
              className="mr-2 mt-2"
            />
          </>
        )}

        {!props.readonly && (
          <>
            <Button
              id="confirm"
              label="I agree"
              onClick={() => onConfirm()}
              className="mr-2 mt-2"
            />
            <Button
              id="back"
              label="Back"
              onClick={() => onBack()}
              className="mr-2 mt-2"
              mode="secondary"
            />
          </>
        )}
      </Spinner>
    </>
  );
};
